import React, { useEffect, useRef } from 'react';

import { useStore } from '../store';
import { useDialog } from './Dialog';
import { SELECTABLE_LANGS } from '../i18n';
import type { RepeatKey, SpeedKey } from '../types';

interface Props {
  open: boolean;
  onClose: () => void;
}

const SPEEDS: Array<[string, SpeedKey]> = [
  ['0.7x', 'slow'],
  ['1.0x', 'normal'],
  ['1.3x', 'fast'],
];

export default function SettingsModal({ open, onClose }: Props) {
  const s = useStore();
  const dialog = useDialog();
  const p = s.progress;
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const repeats: Array<[string, RepeatKey]> = [
    [s.t('settings.repeat_option', { n: 1 }), 'one'],
    [s.t('settings.repeat_option', { n: 2 }), 'twice'],
    [s.t('settings.repeat_option', { n: 3 }), 'three'],
    [s.t('settings.repeat_option', { n: 5 }), 'five'],
    [s.t('settings.repeat_infinite'), 'infinite'],
  ];

  const onImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;

    let text: string;
    try {
      text = await file.text();
    } catch {
      await dialog.alert(s.t('error.title'), s.t('error.file_read_failed'), s.t('action.close'));
      return;
    }

    // Importing replaces everything, so make the user confirm the trade first.
    const preview = text;
    const incoming = countLessons(preview);
    if (incoming === null) {
      await dialog.alert(s.t('error.title'), s.t('data.import_failed'), s.t('action.close'));
      return;
    }
    const ok = await dialog.confirm(
      s.t('data.import_confirm_title'),
      s.t('data.import_confirm_body', { current: s.lessons.length, incoming }),
      {
        confirmLabel: s.t('data.import_button'),
        cancelLabel: s.t('dialog.cancel'),
        destructive: true,
      },
    );
    if (!ok) return;

    const result = s.importData(preview);
    if (!result) {
      await dialog.alert(s.t('error.title'), s.t('data.import_failed'), s.t('action.close'));
      return;
    }
    onClose();
    await dialog.alert(
      s.t('dialog.success_title'),
      s.t('data.import_done', { count: result.lessons }),
      s.t('action.close'),
    );
  };

  return (
    <div
      className="backdrop backdrop--sheet"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="sheet" role="dialog" aria-modal="true" aria-label={s.t('screen.settings.title')}>
        <h2 className="sheet__h">{s.t('settings.display_options')}</h2>
        <Toggle label={s.t('settings.show_english')} value={p.show_word} onToggle={s.toggleWord} />
        <Toggle label={s.t('settings.show_meaning')} value={p.show_meaning} onToggle={s.toggleMeaning} />
        <Toggle
          label={s.t('settings.auto_advance')}
          value={p.auto_advance}
          onToggle={() => s.setAutoAdvance(!p.auto_advance)}
        />

        <h2 className="sheet__h">{s.t('settings.playback_speed')}</h2>
        <div className="chips">
          {SPEEDS.map(([label, val]) => (
            <Radio
              key={val}
              label={label}
              active={p.repeat_speed === val}
              onSelect={() => s.setSpeed(val)}
            />
          ))}
        </div>

        <h2 className="sheet__h">{s.t('settings.repeat_count')}</h2>
        <div className="chips">
          {repeats.map(([label, val]) => (
            <Radio
              key={val}
              label={label}
              active={p.repeat_count === val}
              onSelect={() => s.setRepeatCount(val)}
            />
          ))}
        </div>

        <h2 className="sheet__h">{s.t('settings.language_label')}</h2>
        <div className="chips">
          <Radio
            label={s.t('settings.language_system')}
            active={s.langPref === 'system'}
            onSelect={() => s.setLang('system')}
          />
          {SELECTABLE_LANGS.map(([code, name]) => (
            <Radio key={code} label={name} active={s.langPref === code} onSelect={() => s.setLang(code)} />
          ))}
        </div>

        {/* Web-only: browser storage is cleared with site data, so backups matter. */}
        <h2 className="sheet__h">{s.t('data.section_label')}</h2>
        <div className="btn-row">
          <button type="button" className="btn btn--outline btn--small" onClick={s.exportData}>
            {s.t('data.export_button')}
          </button>
          <button
            type="button"
            className="btn btn--outline btn--small"
            onClick={() => fileRef.current?.click()}
          >
            {s.t('data.import_button')}
          </button>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          className="visually-hidden"
          onChange={onImportFile}
        />

        <button type="button" className="btn btn--primary btn--block" style={{ marginTop: '1.5rem' }} onClick={onClose}>
          {s.t('action.close')}
        </button>
      </div>
    </div>
  );
}

/** Count lessons in an export without committing to the import. */
function countLessons(text: string): number | null {
  try {
    const data = JSON.parse(text);
    if (Array.isArray(data)) return data.length;
    if (data && typeof data === 'object' && Array.isArray(data.lessons)) return data.lessons.length;
    return null;
  } catch {
    return null;
  }
}

function Toggle({ label, value, onToggle }: { label: string; value: boolean; onToggle: () => void }) {
  return (
    <button type="button" className="check-row" onClick={onToggle} role="switch" aria-checked={value}>
      <span className={`checkbox${value ? ' is-on' : ''}`} aria-hidden="true">
        ✓
      </span>
      <span className="check-row__label">{label}</span>
    </button>
  );
}

function Radio({ label, active, onSelect }: { label: string; active: boolean; onSelect: () => void }) {
  return (
    <button type="button" className="chip" aria-pressed={active} onClick={onSelect}>
      {label}
    </button>
  );
}
